import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { useRefresh } from '../lib/data.ts';
import { first, money } from '../lib/format.ts';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';

interface Preview {
  order: { id: string; status: string; totalCents: number; tipCents: number; feesCents: number; appointmentId: string | null; client: string | null; service: string | null };
  payments: Array<{ method: string; amountCents: number }>;
  partners: Array<{ professionalId: string; name: string; reverseCents: number; balanceAfterCents: number }>;
  warnings: Array<{ code: string; level: 'block' | 'warn' | 'info'; text: string }>;
  canReverse: boolean;
  needsPixConfirm: boolean;
}

const METHOD: Record<string, string> = { pix: 'Pix', cash: 'Dinheiro', debit: 'Débito', credit: 'Crédito', client_credit: 'Crédito da cliente', later: 'Pagar depois', package: 'Pacote', gift: 'Vale-presente' };
const WHY = ['Valor errado', 'Forma de pagamento errada', 'Serviço errado', 'Cobrança em duplicidade', 'Outro'];

// Estornar uma cobrança ou corrigi-la (estorna e reabre o atendimento para cobrar de novo).
// A folha mostra o efeito antes de confirmar: o que volta, o que sai do repasse e os avisos.
export function EstornoSheet({ orderId }: { orderId: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const q = useQuery({ queryKey: ['reverse-preview', orderId], queryFn: () => api.get<Preview>(`/orders/${orderId}/reverse-preview`) });
  const [why, setWhy] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const [pix, setPix] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = q.data;
  const reason = why === 'Outro' ? other.trim() : why ?? '';
  const ready = !!p && p.canReverse && reason.length >= 3 && (!p.needsPixConfirm || pix) && !busy;

  async function run(kind: 'reverse' | 'correct') {
    if (!p) return;
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean; appointmentId?: string }>(`/orders/${orderId}/${kind}`, { reason, pixReturned: pix });
      await refresh();
      if (kind === 'correct' && r.appointmentId) {
        toast(`Cobrança de ${first(p.order.client ?? '')} desfeita. Cobre de novo com o valor certo.`);
        nav.open({ kind: 'comanda', id: r.appointmentId });
      } else {
        toast(`Cobrança de ${p.order.client ? first(p.order.client) : 'cliente'} estornada.`);
        nav.close();
      }
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }

  if (!p) return <Sheet title="Estornar cobrança">{q.isError ? <Failed compact error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />}</Sheet>;
  const blocked = p.warnings.filter((w) => w.level === 'block');
  return (
    <Sheet title="Estornar ou corrigir" sub={[p.order.client, p.order.service].filter(Boolean).join(' · ')}
      actions={<>
        <button className="cta" disabled={!ready || !p.order.appointmentId} onClick={() => void run('correct')}>{busy ? 'Salvando…' : 'Corrigir: desfazer e cobrar de novo'}</button>
        <button className="link-back" disabled={!ready} onClick={() => void run('reverse')}>Só estornar, sem cobrar de novo</button>
      </>}>
      <p className="gl-title">O que volta</p>
      <div className="gl">
        {p.payments.map((x, i) => <div key={i} className="gl-row"><span>{METHOD[x.method] ?? x.method}</span><b>− {money(x.amountCents)}</b></div>)}
        <div className="gl-row"><span>Total da cobrança</span><b>{money(p.order.totalCents)}</b></div>
      </div>
      {p.partners.length > 0 && (
        <>
          <p className="gl-title">Repasse da parceira</p>
          <div className="gl">
            {p.partners.map((x) => <div key={x.professionalId} className="gl-row"><span>{first(x.name)}<small>saldo depois: {money(x.balanceAfterCents)}</small></span><b>− {money(x.reverseCents)}</b></div>)}
          </div>
        </>
      )}
      {p.warnings.length > 0 && (
        <div className="est-warns">
          {p.warnings.map((w) => <div key={w.code} className={'note' + (w.level === 'info' ? '' : ' warn')}><Icon name={w.level === 'block' ? 'alert' : 'info'} /><span>{w.text}</span></div>)}
        </div>
      )}
      {!blocked.length && (
        <>
          <p className="gl-title">Motivo</p>
          <div className="chipset">{WHY.map((x) => <button key={x} className={why === x ? 'on' : ''} aria-pressed={why === x} onClick={() => setWhy(x)}>{x}</button>)}</div>
          {why === 'Outro' && <div className="field" style={{ marginTop: 8 }}><label htmlFor="estWhy">Conte o motivo</label><input id="estWhy" className="text-in" autoFocus value={other} onChange={(e) => setOther(e.target.value)} /></div>}
          {p.needsPixConfirm && (
            <div className="toggle-line" style={{ marginTop: 12 }}><span><strong>Já devolvi o Pix à cliente</strong><small>Este Pix já foi conferido no extrato.</small></span>
              <button role="switch" aria-checked={pix} aria-label="Já devolvi o Pix" onClick={() => setPix(!pix)}><Icon name={pix ? 'toggleOn' : 'toggleOff'} /></button></div>
          )}
          <p className="m-hint">Corrigir estorna esta cobrança e abre a cobrança de novo, com os mesmos serviços, para você ajustar o valor ou a forma de pagamento. Tudo fica registrado.</p>
        </>
      )}
    </Sheet>
  );
}
